/**
 * useGameEvents — React-Query hook for the FULL (untruncated) event log.
 *
 * The main game state wire (`GET /games/:id` and WebSocket updates) caps
 * `state.events` to a recent tail (WIRE_EVENT_LIMIT) for bandwidth reasons.
 * This hook fetches the full history from `GET /api/v1/games/:id/events` which
 * is the only surface that exposes the complete log.
 *
 * Intended consumers:
 *   - The log screen (`app/(app)/games/[id]/log.tsx`) — renders every row and
 *     replays any point via `reconstructTerritoriesAt` / `getEventHighlight`.
 *   - The in-game time-machine (`games/[id]/index.tsx`) — fetched LAZILY (via
 *     `opts.enabled`) only while a snapshot is open, so normal play never pulls
 *     the full log.
 *
 * Non-consumers (stay on the inline tail):
 *   - Animation hooks, the live `RecentActivitySidebar` glance, and
 *     `newPlayerOutNotices` — all legitimately want only the recent tail.
 *
 * Returns:
 *   events      — full event array (empty array while loading or on error)
 *   eventCount  — authoritative total from the server
 *   isLoading   — true on the initial fetch
 *   isRefetching — true on subsequent background refetches
 *   error       — React-Query error object, or null
 *   refetch     — callable to trigger a manual re-fetch (wired to PTR)
 *
 * LOBBY games (or games with no state) return `{ events: [], eventCount: 0 }`.
 * The hook is disabled when `id` is undefined/empty.
 */

import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { mergeEventDelta } from '../lib/eventDelta'
import type { GameEventsResponse } from '@warcarp/api-types'

const QK = {
  events: (id: string) => ['games', id, 'events'] as const,
}

const fetchEvents = (id: string, since: number) =>
  apiGet<GameEventsResponse>(
    `/api/v1/games/${id}/events${since > 0 ? `?since=${since}` : ''}`,
  )

export function useGameEvents(
  id: string | undefined,
  opts?: {
    enabled?: boolean
    /**
     * When this changes, the cached log is dropped and the next read is a
     * full one. Fog-of-war games pass their status: the log cached during
     * play is the viewer's redacted copy, and the delta fetch would only
     * append to it once the game ends and the fog lifts.
     */
    resetKey?: string
  },
) {
  const queryClient = useQueryClient()
  const resetKey = opts?.resetKey
  const lastResetKey = useRef(resetKey)
  useEffect(() => {
    if (lastResetKey.current === resetKey) return
    lastResetKey.current = resetKey
    if (id) queryClient.removeQueries({ queryKey: QK.events(id) })
  }, [id, queryClient, resetKey])
  // Caller can gate the fetch (e.g. the in-game time-machine only wants the
  // full log while a snapshot is open). Defaults to enabled when `id` is set.
  const enabled = !!id && (opts?.enabled ?? true)
  const { data, isLoading, isRefetching, error, refetch } = useQuery<GameEventsResponse>({
    queryKey: QK.events(id ?? ''),
    // DELTA FETCH: read the cached prefix, ask only for events after it, and
    // append. mergeEventDelta flags any case where it can't prove the result is
    // the complete gap-free log; we then resync with a full read. So a refetch
    // after a few turns costs ~the new events, not the whole log — and we can
    // never silently miss an event.
    queryFn: async () => {
      const cached = queryClient.getQueryData<GameEventsResponse>(QK.events(id ?? ''))?.events ?? []
      const resp = await fetchEvents(id ?? '', cached.length)
      const merged = mergeEventDelta(cached, resp)
      if (merged.needsResync) {
        const full = await fetchEvents(id ?? '', 0)
        return { events: full.events, eventCount: full.eventCount, since: 0 }
      }
      return { events: merged.events, eventCount: resp.eventCount, since: resp.since ?? 0 }
    },
    enabled,
    // 60 s staleTime: the log is append-only. A user who opened the log
    // screen 30 seconds ago won't see new events without PTR anyway, and
    // polling on a tight interval would be wasteful. PTR / the snapshot's
    // refetch-when-behind drive on-demand freshness (cheaply, via the delta).
    staleTime: 60_000,
    // Keep the per-query window-focus behavior disabled; the root
    // AppLifecycleHandler explicitly refreshes active queries when the app
    // returns from a meaningful background stay, including this long-lived
    // event-log query.
    refetchOnWindowFocus: false,
  })

  return {
    events: data?.events ?? [],
    eventCount: data?.eventCount ?? 0,
    isLoading,
    isRefetching,
    error: error ?? null,
    refetch,
  }
}
